// Step 3 — Campaign picker.
//
// Per-path filtering rules (2026-08-12, owner directive):
//   product      → kind=product,     show ALL, badge intersection
//   promotional  → kind=promotional, show ALL, badge intersection
//   category     → NO kind filter,   hard-filter to campaigns whose
//                  matchedProductIds contains a product whose
//                  categoryRef ∈ selected categories. Empty state is
//                  legitimate — the operator sees no campaigns when
//                  nothing matches.
//   brand        → kind=brand,       no intersection concept
//
// Intersection is computed server-side (routes/campaigns.js) via new
// ?intersectProductIds / ?intersectCategoryIds params. Category ids
// resolve to product ids via CatalogProduct.categoryRef there, so the
// frontend just passes what the operator picked.

import { useEffect, useMemo, useState } from 'react';
import {
  Box, VStack, HStack, Text, Badge, Radio, RadioGroup, Spinner,
  Button, Card, CardBody
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import type { WizardSelections } from './types';

type Campaign = {
  id:                   string;
  platform:             string;
  externalId:           string;
  name:                 string;
  status:               string | null;
  objective:            string | null;
  kind:                 string | null;
  matchedProductCount:  number;
  intersectionCount:    number;  // populated by backend when intersect params sent
  adSetCount:           number;
  adCount:              number;
  renderedAdCount:      number;
  lastSyncedAt:         string | null;
};

type Props = {
  value:                WizardSelections;
  onChange:             (patch: Partial<WizardSelections>) => void;
  onGoToQuickCreate:    () => void;
};

// Per-path query builder. Returns the query string suffix (without
// leading &) so the effect below can compose it with the base
// brandId= param. Deliberate that category path passes NO kind — see
// header comment.
function buildCampaignQuery(value: WizardSelections): string {
  const parts: string[] = [];
  if (value.path === 'brand') {
    parts.push('kind=brand');
  } else if (value.path === 'product') {
    parts.push('kind=product');
    if (value.productIds.length) {
      parts.push(`intersectProductIds=${value.productIds.join(',')}`);
    }
  } else if (value.path === 'promotional') {
    parts.push('kind=promotional');
    // Promotional path uses calloutProductIds when the operator ticked
    // optional callouts; otherwise there's nothing to intersect on and
    // every promotional campaign is a valid pick.
    if (value.calloutProductIds.length) {
      parts.push(`intersectProductIds=${value.calloutProductIds.join(',')}`);
    }
  } else if (value.path === 'category') {
    // No kind filter — a product-kind campaign whose SKUs live in the
    // picked category is a valid hit. Hard filter via
    // filterEmptyIntersection so a zero-hit brand shows zero campaigns.
    if (value.categoryIds.length) {
      parts.push(`intersectCategoryIds=${value.categoryIds.join(',')}`);
      parts.push('filterEmptyIntersection=true');
    }
  }
  return parts.join('&');
}

// Per-path copy so the empty state + header speak the operator's
// mental model. Nothing else uses these.
const PATH_HEADERS: Record<WizardSelections['path'], { header: string; helper: string; emptyTitle: string; emptyHint: string }> = {
  product: {
    header:     'Pick a product campaign',
    helper:     'Every product-kind campaign is shown. Intersecting campaigns (those that already include one of your picked products) rank first with a badge.',
    emptyTitle: 'No product campaigns yet',
    emptyHint:  'Create one now — the wizard will pin your selected products automatically.'
  },
  promotional: {
    header:     'Pick a promotional campaign',
    helper:     'Every promotional-kind campaign is shown. Callout intersections rank first with a badge.',
    emptyTitle: 'No promotional campaigns yet',
    emptyHint:  'Create one — the wizard will pin your callouts automatically.'
  },
  brand: {
    header:     'Pick a brand campaign',
    helper:     'Only brand-kind campaigns are shown.',
    emptyTitle: 'No brand campaigns yet',
    emptyHint:  'Create one — the wizard will attach your UGC as brand identity.'
  },
  category: {
    header:     'Pick a campaign whose products live in the selected categories',
    helper:     'Any campaign kind qualifies — filtered to those whose matched products overlap with your categories.',
    emptyTitle: 'No campaigns match the selected categories',
    emptyHint:  'No existing campaign has products in these categories. Pick a different category, or create a new campaign below.'
  }
};

export function Step3Campaign({ value, onChange, onGoToQuickCreate }: Props) {
  const { activeBrand } = useBrand();
  const [rows, setRows]       = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Recompose the query string on any input that affects it so the
  // list refreshes when the operator adjusts picks and hops between
  // Step 2 and Step 3.
  const query = buildCampaignQuery(value);
  const pathHeaders = PATH_HEADERS[value.path];

  useEffect(() => {
    if (!activeBrand?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const url = `/api/campaigns?brandId=${encodeURIComponent(activeBrand.id)}${query ? `&${query}` : ''}`;
        const res = await apiJson<{ campaigns: Campaign[] }>(url);
        if (cancelled) return;
        setRows(res.campaigns || []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load campaigns');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id, query]);

  // Backend already sorts by intersection DESC when intersect params
  // sent. Nothing else to do client-side; the list is authoritative.
  const filtered = useMemo(() => rows, [rows]);

  // Only paths that carry an intersection concept surface the badge.
  const showsIntersection = value.path === 'product' || value.path === 'promotional' || value.path === 'category';

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align="flex-end">
        <Box>
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
            {pathHeaders.header}
          </Text>
          <Text fontSize="xs" color="brand.muted" mt={1}>
            {pathHeaders.helper}
          </Text>
        </Box>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            // Empty campaignId + populate newCampaign so the shell's
            // linear-nav sees a name and routes forward to quickcreate.
            onChange({ campaignId: null, newCampaign: { ...value.newCampaign, name: value.newCampaign.name || 'New campaign' } });
            onGoToQuickCreate();
          }}
        >
          + New campaign
        </Button>
      </HStack>

      {loading && <HStack py={4}><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading campaigns…</Text></HStack>}
      {error && <Text fontSize="sm" color="red.600">{error}</Text>}

      {!loading && filtered.length === 0 && (
        <Card variant="outline">
          <CardBody>
            <VStack align="stretch" spacing={2} py={4} textAlign="center">
              <Text fontSize="sm" fontWeight="700" color="brand.ink">
                {pathHeaders.emptyTitle}
              </Text>
              <Text fontSize="xs" color="brand.muted">
                {pathHeaders.emptyHint}
              </Text>
              <Box>
                <Button
                  size="sm"
                  variant="brand"
                  onClick={() => {
                    onChange({ campaignId: null, newCampaign: { ...value.newCampaign, name: value.newCampaign.name || 'New campaign' } });
                    onGoToQuickCreate();
                  }}
                >
                  + New campaign
                </Button>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      )}

      {filtered.length > 0 && (
        <RadioGroup
          value={value.campaignId || ''}
          onChange={(id) => onChange({ campaignId: id, newCampaign: { ...value.newCampaign, name: '' } })}
        >
          <VStack align="stretch" spacing={2}>
            {filtered.map(c => {
              const hasIntersection = showsIntersection && c.intersectionCount > 0;
              return (
                <Box
                  key={c.id}
                  borderWidth="1px"
                  borderColor={value.campaignId === c.id ? 'rsViolet.500' : 'brand.border'}
                  bg={value.campaignId === c.id ? 'rsViolet.50' : 'white'}
                  borderRadius="md"
                  px={3}
                  py={2}
                >
                  <HStack align="flex-start" spacing={3}>
                    <Radio value={c.id} mt={1} />
                    <Box flex={1} minW={0}>
                      <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize="sm" fontWeight="800" color="brand.ink" noOfLines={1}>
                          {c.name}
                        </Text>
                        <Badge fontSize="9px" colorScheme="purple" variant="subtle">
                          {c.platform}
                        </Badge>
                        {c.kind && (
                          <Badge fontSize="9px" colorScheme="gray" variant="subtle">
                            {c.kind}
                          </Badge>
                        )}
                        {c.status && (
                          <Badge fontSize="9px" colorScheme="gray" variant="subtle">
                            {c.status}
                          </Badge>
                        )}
                        {/* Intersection badge — only paths that pass
                            intersect params render it (product /
                            promotional show all campaigns; the badge is
                            the "relevant" signal). Green = strong
                            overlap; muted grey when the campaign has
                            products but none intersect the picks. */}
                        {hasIntersection && (
                          <Badge fontSize="9px" colorScheme="green" variant="solid">
                            {c.intersectionCount} of {c.matchedProductCount} matched
                          </Badge>
                        )}
                      </HStack>
                      <HStack spacing={3} mt={1}>
                        <Text fontSize="10px" color="brand.muted">
                          {c.matchedProductCount} matched product{c.matchedProductCount === 1 ? '' : 's'}
                        </Text>
                        <Text fontSize="10px" color="brand.muted">
                          {c.renderedAdCount} ad{c.renderedAdCount === 1 ? '' : 's'} rendered
                        </Text>
                      </HStack>
                    </Box>
                  </HStack>
                </Box>
              );
            })}
          </VStack>
        </RadioGroup>
      )}
    </VStack>
  );
}
